import type {
  ComponentProps,
  FunctionComponent,
  MouseEvent,
  RefObject,
} from 'react';
import React, { useEffect, useRef } from 'react';
import cx from 'classnames';

import { Tooltip } from '@trello/nachos/tooltip';
import { useSharedState } from '@trello/shared-state';
import { LabelsPopoverTestIds } from '@trello/test-ids';
import { useGlobalTheme } from '@trello/theme';

import type { CardLabelType } from './CardLabel.types';
import { formatLabelTooltip } from './formatLabelTooltip';
import { flattenLabelColor } from './labelColors';
import { mergeRefs } from './mergeRefs';
import { showLabelsState } from './showLabelsState';
import { useDraggableLabel } from './useDraggableLabel';

import styles from './CompactCardLabel.less';

export interface CompactCardLabelProps {
  label: CardLabelType;
  className?: string;
  dataTestClass?: string;
  /**
   * Whether or not the label can be collapsed. In collapsed mode, the label
   * name is hidden. This is controlled by `showLabelsState`, which listens to
   * a localStorage entry called `labelState`, and also has a :global CSS class
   * parallel in `.body-card-label-text-on` and `-off `. Set this to `false` to
   * opt out of the global behavior and force the label's text to always show.
   * @default true
   */
  isCollapsible?: boolean;
  /**
   * Whether or not the label can be dragged and dropped into a different place.
   * The drop target has to handle the native drop event itself; see
   * app/scripts/views/card/CardViewAttachment.js::dropLabel for an example.
   * @default false
   */
  isDraggable?: boolean;
  /**
   * Whether or not to enable hover states for the label output. Adding an
   * `onClick` handler will have the same output, but this is a fallback in case
   * hover colors are desired without any click interactions.
   * @default false
   */
  isHoverable?: boolean;
  /**
   * If onClick is defined, the label is rendered as a <button> element.
   */
  onClick?: (e: MouseEvent) => void;
  /**
   * Optional tabIndex prop. Set this to -1 to disable keyboard interaction.
   * @default 0
   */
  tabIndex?: HTMLElement['tabIndex'];
  /**
   * Overrides the tooltip content.
   */
  tooltipContent?: ComponentProps<typeof Tooltip>['content'];
}

export const CompactCardLabel: FunctionComponent<CompactCardLabelProps> = ({
  label,
  className: propsClassName,
  dataTestClass,
  isCollapsible = true,
  isDraggable,
  isHoverable,
  tabIndex = 0,
  tooltipContent,
  onClick,
}) => {
  const [{ showText }] = useSharedState(showLabelsState);
  const isCollapsed = isCollapsible && !showText;

  const ref = useRef<HTMLButtonElement | HTMLDivElement | null>(null);

  /**
   * Label width is determined by content size, so we set max- and min-width.
   * However, animations are stilted if a max-width is not explicitly set,
   * which causes jumpiness when collapsing. This effect runs on expanded labels
   * to tell the CSS animation what the computed maximum width of the label is.
   */
  useEffect(() => {
    if (!ref.current || isCollapsed) {
      return;
    }
    const offsetWidth = ref.current.offsetWidth;
    const expandedMaxWidth = offsetWidth ? `${offsetWidth}px` : 'auto';
    requestAnimationFrame(() => {
      ref.current?.style.setProperty('--expanded-max-width', expandedMaxWidth);
    });
  }, [isCollapsed, label.name]);

  const { onDragStart, onDragEnd } = useDraggableLabel(label, ref);

  const { effectiveColorMode } = useGlobalTheme();

  // We're moving away from the dot design when a theme is active, as ADS colors
  // have more accessible contrast ratios for labels.
  const shouldShowDot =
    effectiveColorMode === null && !isCollapsed && !!label.color;

  const className = cx(
    styles.compactLabel,
    label.color ? styles[label.color] : styles.colorless,
    isCollapsed && styles['compactLabel--collapsed'],
    (onClick || isHoverable) && styles['compactLabel--hoverable'],
    shouldShowDot && styles['compactLabel--with-dot'],
    label.color && `color-blind-pattern-${flattenLabelColor(label.color)}`,
    propsClassName,
  );
  const formattedTooltip = formatLabelTooltip(label);

  return (
    <Tooltip
      content={tooltipContent ?? formattedTooltip}
      hideTooltipOnMouseDown={true}
    >
      {({ ref: tooltipRef, ...tooltipProps }) => {
        const mergedRefs = mergeRefs(ref, tooltipRef);

        return onClick ? (
          <button
            {...tooltipProps}
            className={className}
            draggable={isDraggable}
            onClick={onClick}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            ref={mergedRefs as RefObject<HTMLButtonElement>}
            tabIndex={tabIndex}
            aria-label={
              typeof tooltipContent === 'string'
                ? tooltipContent
                : formattedTooltip
            }
            data-color={label.color}
            data-expanded={!isCollapsed}
            data-test-class={dataTestClass}
            data-testid={LabelsPopoverTestIds.CompactCardLabel}
          >
            {label.name}
          </button>
        ) : (
          <span
            {...tooltipProps}
            className={className}
            ref={mergedRefs as RefObject<HTMLSpanElement>}
            tabIndex={tabIndex}
            aria-label={
              typeof tooltipContent === 'string'
                ? tooltipContent
                : formattedTooltip
            }
            data-color={label.color}
            data-expanded={!isCollapsed}
            data-test-class={dataTestClass}
            data-testid={LabelsPopoverTestIds.CompactCardLabel}
          >
            {label.name}
          </span>
        );
      }}
    </Tooltip>
  );
};
